import "./Question.less";

import { ActionButtons, ReorderButtons } from "features/common/ActionIcons";
import { LevelIndicator } from "features/qualityForm/common/LevelIndicator";
import { selectQuestionHelpText } from "features/qualityForm/qualityFormSlice";
import QuestionBody from "features/qualityForm/common/questionComponents/QuestionBody";
import QuestionName from "features/qualityForm/common/questionComponents/QuestionName";
import { QuestionSettingsIndicators } from "features/qualityForm/common/questionComponents/SettingsIndicators";
import { useState } from "react";
import {
    AssessmentFormQuestionTypeEnum,
    SurveyQuestionTypeEnum,
} from "@genesys/cloud-frontend/dist/mjs/models";
import QuestionEditor from "./QuestionEditor";
import {
    DependentIds,
    EntityType,
    initiateQuestionDeletion,
    moveQuestionDownIfValid,
    moveQuestionUpIfValid,
    selectQuestionGroup,
    selectQuestionGroupIdContainingQuestion,
    VisibilityConflictsOnMove,
    selectCanEditForm,
    qualityFormActions,
} from "features/qualityForm/qualityFormSlice";
import { selectQuestion } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { ErrorAction } from "../common/ErrorBox/VisibilityConflictErrorBox";
import { shallowEqual } from "react-redux";
import { VisibilityConflictErrorBoxContainer } from "../common/ErrorBox/VisibilityConflictErrorBoxContainer";

import { FormLocaleProvider } from "features/i18n/useLocale";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { AgreementRate } from "../AgreementRate";
import { selectQuestionAgreementRateDisplayState } from "features/qualityForm/qualityFormAgreementRateSelectors";
import { useStrings } from "features/i18n/useStrings";
import strings from "./Question.strings";
import { announceAfterStateUpdate } from "features/qualityForm/utils/screenReaderAnnouncement";

export function QuestionSummary({
    questionId,
    onEdit,
    onDelete,
}: {
    questionId: string;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const [visibilityError, setVisibilityError] = useState<{
        visibilityDependentIds: VisibilityConflictsOnMove;
        errorAction: ErrorAction;
    }>();

    const { canMoveUp, canMoveDown, questionIndex, questionGroupIndex } =
        useAppSelector((state) => {
            const questionGroupId = selectQuestionGroupIdContainingQuestion(
                questionId
            )(state.qualityForm);
            if (!questionGroupId) {
                return {
                    canMoveUp: false,
                    canMoveDown: false,
                    questionIndex: -1,
                    questionGroupIndex: 0,
                };
            }
            const questions =
                selectQuestionGroup(state, questionGroupId)?.questions ?? [];
            const questionPosition = questions?.indexOf(questionId);
            const formQuestionGroupIndex =
                state.qualityForm.data.form?.questionGroups?.indexOf(
                    questionGroupId
                );
            return {
                canMoveUp: questionPosition > 0,
                canMoveDown: questionPosition < questions.length - 1,
                questionIndex: questionPosition,
                questionGroupIndex:
                    formQuestionGroupIndex !== undefined
                        ? formQuestionGroupIndex
                        : 0,
            };
        }, shallowEqual);

    const canEditForm = useAppSelector((state) => {
        return selectCanEditForm(state);
    });

    const question = useAppSelector((state) =>
        selectQuestion(state, questionId)
    );

    const displayQuestionBody =
        question &&
        question.type !== SurveyQuestionTypeEnum.FreeTextQuestion &&
        question.type !== AssessmentFormQuestionTypeEnum.FreeTextQuestion;

    const helpText = useAppSelector((state) =>
        selectQuestionHelpText(state, questionId)
    );

    const questionAgreementRateDisplayState = useAppSelector((state) =>
        selectQuestionAgreementRateDisplayState(state, questionId)
    );

    return (
        <VisibilityConflictErrorBoxContainer
            visibilityConflictsOnMove={visibilityError?.visibilityDependentIds}
            clearVisibilityConflicts={() => setVisibilityError(undefined)}
            entityType={EntityType.Question}
            errorAction={visibilityError?.errorAction ?? ErrorAction.MoveUp}
        >
            <div className="QuestionSummary">
                <div className="actions">
                    <LevelIndicator inEditMode={canEditForm} />
                    {canEditForm && (
                        <ReorderButtons
                            onMoveUp={
                                canMoveUp && question
                                    ? () => {
                                          const visibilityDependentIds =
                                              dispatch(
                                                  moveQuestionUpIfValid(
                                                      questionId
                                                  )
                                              ) as unknown as DependentIds;

                                          // Only announce if move was successful (no conflicts)
                                          if (
                                              !visibilityDependentIds
                                                  ?.questionIds?.length &&
                                              !visibilityDependentIds
                                                  ?.questionGroupIds?.length
                                          ) {
                                              // After moving up, the question moves from questionIndex to questionIndex - 1 (0-based)
                                              // New position in 1-based: questionIndex (which is the old position - 1)
                                              const newPosition1Based =
                                                  questionIndex;
                                              announceAfterStateUpdate(
                                                  dispatch,
                                                  qualityFormActions.setScreenReaderAnnouncement,
                                                  qualityFormActions.clearScreenReaderAnnouncement,
                                                  t.screenreader.questionMoveUp(
                                                      {
                                                          currentQuestionGroupIndex:
                                                              questionGroupIndex +
                                                              1,
                                                          currentPosition:
                                                              questionIndex + 1,
                                                          newPosition:
                                                              newPosition1Based,
                                                          questionName:
                                                              question.text ??
                                                              "",
                                                      }
                                                  )
                                              );
                                          }

                                          setVisibilityError({
                                              visibilityDependentIds,
                                              errorAction: ErrorAction.MoveUp,
                                          });
                                      }
                                    : undefined
                            }
                            onMoveDown={
                                canMoveDown && question
                                    ? () => {
                                          const visibilityDependentIds =
                                              dispatch(
                                                  moveQuestionDownIfValid(
                                                      questionId
                                                  )
                                              ) as unknown as DependentIds;

                                          // Only announce if move was successful (no conflicts)
                                          if (
                                              !visibilityDependentIds
                                                  ?.questionIds?.length &&
                                              !visibilityDependentIds
                                                  ?.questionGroupIds?.length
                                          ) {
                                              // After moving down, the question moves from questionIndex to questionIndex + 1 (0-based)
                                              // New position in 1-based: questionIndex + 2
                                              const newPosition1Based =
                                                  questionIndex + 2;
                                              announceAfterStateUpdate(
                                                  dispatch,
                                                  qualityFormActions.setScreenReaderAnnouncement,
                                                  qualityFormActions.clearScreenReaderAnnouncement,
                                                  t.screenreader.questionMoveDown(
                                                      {
                                                          currentQuestionGroupIndex:
                                                              questionGroupIndex +
                                                              1,
                                                          currentPosition:
                                                              questionIndex + 1,
                                                          newPosition:
                                                              newPosition1Based,
                                                          questionName:
                                                              question.text ??
                                                              "",
                                                      }
                                                  )
                                              );
                                          }

                                          setVisibilityError({
                                              visibilityDependentIds,
                                              errorAction: ErrorAction.MoveDown,
                                          });
                                      }
                                    : undefined
                            }
                        />
                    )}
                </div>
                <div className="content">
                    <div className="heading">
                        <div className="title h3">
                            <QuestionName questionId={questionId} />
                        </div>

                        {!canEditForm &&
                            questionAgreementRateDisplayState.shouldDisplay &&
                            questionAgreementRateDisplayState.data && (
                                <div
                                    className="question-agreement-rate"
                                    data-testid="question-agreement-rate"
                                >
                                    <AgreementRate
                                        agreementRate={
                                            questionAgreementRateDisplayState
                                                .data.agreementRate
                                        }
                                        hasData={
                                            questionAgreementRateDisplayState
                                                .data.hasData
                                        }
                                    />
                                </div>
                            )}

                        {canEditForm && (
                            <ActionButtons
                                helpText={helpText}
                                onEdit={onEdit}
                                onDelete={() => {
                                    onDelete();
                                }}
                            />
                        )}
                    </div>
                    <div className="indicators">
                        <QuestionSettingsIndicators questionId={questionId} />
                    </div>
                    {displayQuestionBody && (
                        <FormLocaleProvider>
                            <QuestionBody
                                questionId={questionId}
                                isQuestionEditable={false}
                            />
                        </FormLocaleProvider>
                    )}
                </div>
            </div>
        </VisibilityConflictErrorBoxContainer>
    );
}

export function Question({ questionId }: { questionId: string }) {
    const showEditMode = useAppSelector(
        (state) =>
            state.qualityForm.data.questions?.[questionId].showEditMode ?? false
    );

    const dispatch = useAppDispatch();
    const [visibilityDependentIds, setVisibilityDependentIds] =
        useState<DependentIds>();
    const onDelete = () => {
        const visibilityConflicts = dispatch(
            initiateQuestionDeletion(questionId)
        ) as unknown as DependentIds;
        if (
            visibilityDependentIds?.questionGroupIds.length !== 0 ||
            visibilityDependentIds?.questionIds.length !== 0
        ) {
            setVisibilityDependentIds(visibilityConflicts);
        }
    };

    return (
        <VisibilityConflictErrorBoxContainer
            visibilityDependentIds={visibilityDependentIds}
            clearVisibilityConflicts={() =>
                setVisibilityDependentIds(undefined)
            }
            entityType={EntityType.Question}
            errorAction={ErrorAction.Delete}
        >
            <div className="Question">
                {showEditMode ? (
                    <QuestionEditor
                        questionId={questionId}
                        onDone={() => {
                            dispatch(
                                qualityFormActions.switchEditModeForQuestion({
                                    questionId,
                                    showEditMode: false,
                                })
                            );
                        }}
                        onDelete={onDelete}
                    />
                ) : (
                    <QuestionSummary
                        questionId={questionId}
                        onEdit={() =>
                            dispatch(
                                qualityFormActions.switchEditModeForQuestion({
                                    questionId,
                                    showEditMode: true,
                                })
                            )
                        }
                        onDelete={onDelete}
                    />
                )}
            </div>
        </VisibilityConflictErrorBoxContainer>
    );
}
